import React from 'react';
import { connect } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';

import { State } from '../../lib/reducers';
import getRouteFor, { Route } from '../../lib/utils/router';
import LocalStorage from '../../localStorage';
import { isRecorder } from '../../lib/reduxSelectors/session';

import StyledMenu from '../StyledMenu';
import ParticipantsCount from './ParticipantsCount';
import AvatarNew from '../AvatarNew';
import Logout from '../Logout';
import MyRecordings from '../MyRecordings';
import UserProfile from '../UserProfile';
import UserPins from '../UserPins';
import UserLiveStreaming from '../UserLiveStreaming';
import SiteTitle from '../SiteTitle';
import { IconLogout } from '../IconSet';
import Actions from './Actions';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      zIndex: theme.zIndex.modal - 1,
    },
    partCount: {
      flexGrow: 1,
    },
    appBar: {
      backgroundColor: theme.palette.background.paper,
    },
    accountButton: {
      '&:hover': {
        backgroundColor: "transparent",
      }
    },
    menu: {
      padding: theme.spacing(1),
    },
    divider: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    item: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    }
  }),
);


const messages = defineMessages({
  logout: { id: 'logout' },
  userInfo: { id: 'userInformation' },
  pins: { id: 'userPins' },
  streamingSettings: { id: 'streamingSettings' },
  recordingsList: { id: 'reportRecording' },
  holocomAccount: { id: 'holocomAccount' },
});


type MappedProps = {
  isAuthenticated: boolean;
  displayName: string;
  isRecorder: boolean;
  isLivestreamingEnabled: boolean;
  isRecordingEnabled: boolean;
  siteTitle: string;
  loginEnabled: boolean;
}


type Props = {
  localStore: LocalStorage;
  url?: string;
}

type ExtendedProps = Props & MappedProps & RouteComponentProps;


const mapStateToProps = (state: State, { url }: Props): MappedProps => ({
  isAuthenticated: state.auth.isAuthenticated,
  displayName: state.session.displayName || "",
  isRecorder: isRecorder(state, url),
  isLivestreamingEnabled: state.session.livestreamingEnabled || false,
  isRecordingEnabled: state.session.recordingEnabled || false,
  siteTitle: state.appconfig.site_title,
  loginEnabled: state.appconfig.login_enabled,
});


function Appbar(props: ExtendedProps) {
  const classes = useStyles();

  const { isAuthenticated } = props;

  const homePath = getRouteFor(Route.Home);

  if (props.location.pathname === homePath && !isAuthenticated) {
    return null;
  }

  if (props.isRecorder) return null;

  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar} position="static">
        <Toolbar>
          <SiteTitle />

          <div className={classes.partCount}>
            <ParticipantsCount />
          </div>
          <Actions />
          <AccountMenu {...props} />
        </Toolbar>
      </AppBar>
    </div>
  );
}


function AccountMenu(props: ExtendedProps) {
  const siteTitle = props.siteTitle;
  const classes = useStyles();
  const { isLivestreamingEnabled, isRecordingEnabled, loginEnabled } = props;
  const { formatMessage } = useIntl();

  const { isAuthenticated } = props;

  const [logoutOpen, setLogoutOpen] = React.useState<boolean>(false);
  const [userProfileOpen, setUserProfileOpen] = React.useState<boolean>(false);
  const [myRecordingsOpen, setMyRecordingsOpen] = React.useState<boolean>(false);
  const [userPinsOpen, setUserPinsOpen] = React.useState<boolean>(false);
  const [liveStreamingSettingsOpen, setLiveStreamingSettingsOpen] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = (): void => setAnchorEl(null);

  const logout = (): void => {
    setLogoutOpen(true);
    closeMenu();
  };

  const logoutClose = (): void => {
    setLogoutOpen(false);
    closeMenu();
  };

  const showUserInfo = (): void => {
    setUserProfileOpen(true);
    closeMenu();
  };

  const closeUserInfo = (): void => {
    setUserProfileOpen(false);
    closeMenu();
  };

  const showUserPins = (): void => {
    setUserPinsOpen(true);
    closeMenu();
  };

  const closeUserPins = (): void => {
    setUserPinsOpen(false);
    closeMenu();
  };

  const showLiveStreamingSettings = (): void => {
    setLiveStreamingSettingsOpen(true);
    closeMenu();
  };

  const closeLiveStreamingSettings = (): void => {
    setLiveStreamingSettingsOpen(false);
    closeMenu();
  };

  const showMyRecordings = (): void => {
    setMyRecordingsOpen(true);
    closeMenu();
  };

  const closeMyRecordings = (): void => {
    setMyRecordingsOpen(false);
    closeMenu();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div>
      <Tooltip
        title={
          <React.Fragment>
            <Typography variant="body2">{formatMessage(messages.holocomAccount, { site: siteTitle })}</Typography>
            <Typography variant="caption">{props.displayName}</Typography>
          </React.Fragment>
        }
      >
        <IconButton onClick={handleMenu} className={classes.accountButton}>
          <AvatarNew name={props.displayName} />
        </IconButton>
      </Tooltip>
      <StyledMenu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={closeMenu}
      >
        <div className={classes.menu}>
          <MenuItem className={classes.item} onClick={showUserInfo}>
            {formatMessage(messages.userInfo)}
          </MenuItem>
          <MenuItem className={classes.item} onClick={showUserPins}>
            {formatMessage(messages.pins)}
          </MenuItem>
          { isLivestreamingEnabled &&
            <MenuItem className={classes.item} onClick={showLiveStreamingSettings}>
              {formatMessage(messages.streamingSettings)}
            </MenuItem>
          }
          { isRecordingEnabled &&
            <MenuItem className={classes.item} onClick={showMyRecordings}>
              {formatMessage(messages.recordingsList)}
            </MenuItem>
          }
          { loginEnabled &&
            <React.Fragment>
              <Divider className={classes.divider} />
              <MenuItem className={classes.item} onClick={logout}>
                <ListItemIcon>
                  <IconLogout size={24} />
                </ListItemIcon>
                <ListItemText primary={formatMessage(messages.logout)} />
              </MenuItem>
            </React.Fragment>
          }
        </div>
      </StyledMenu>
      {logoutOpen &&
        <Logout
          localStore={props.localStore}
          onLogoutClick={logout}
          onCancelClick={logoutClose}
        />}
      {myRecordingsOpen && <MyRecordings onCloseClick={closeMyRecordings} />}
      {userProfileOpen && <UserProfile onClose={closeUserInfo} />}
      {userPinsOpen && <UserPins onClose={closeUserPins} />}
      {liveStreamingSettingsOpen && <UserLiveStreaming onClose={closeLiveStreamingSettings} />}
    </div>
  );
}


export default withRouter(connect(mapStateToProps)(Appbar));
